import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { NbDialogRef, NbCardModule, NbSpinnerModule, NbDialogService } from '@nebular/theme';
import { GridApi, GridOptions } from 'ag-grid-enterprise';
import { Subscription } from 'rxjs';
import { first, filter, map, switchMap } from 'rxjs/operators';
import { DocumentsService, SaveToastService, SpreadsheetColumnsService } from '../../../@core/utils';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { NgTemplateOutlet } from '@angular/common';
import { DeleteRendererComponent } from '../ag-grid-renderers/delete-button-renderer.component';
import { BasicDialogComponent } from '../basic-dialog/basic-dialog.component';
import { AgGridAngular } from 'ag-grid-angular';
import { TABLE_FILTER_ICON } from '../../../@core/utils/constants';
import { AddDocumentDialogComponent } from './add-document-dialog/add-document-dialog.component';
import { DocumentSheetComponent } from '../document-sheet/document-sheet.component';
import { RightPanelDialogService } from '../../../@core/utils/right-panel-dialog.service';

@Component({
    selector: 'app-ag-grid-documents',
    templateUrl: './ag-grid-documents.component.html',
    styleUrls: ['./ag-grid-documents.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
      NbCardModule,
      AgGridAngular,
      NbSpinnerModule,
      TranslateModule,
      NgTemplateOutlet,
    ],
})
export class AgGridDocumentsComponent implements OnInit {

  constructor(
    private ref: NbDialogRef<AgGridDocumentsComponent>,
    private spreadsheetColumnsService: SpreadsheetColumnsService,
    private translate: TranslateService,
    private cdRef: ChangeDetectorRef,
    private documentsService: DocumentsService,
    private dialogService: NbDialogService,
    private saveToast: SaveToastService,
    private sideFormLevelService: RightPanelDialogService,
  ) { }

  @Input() public title: string;
  @Input() public assetIds: number[] = [];
  @Input() public investIds: number[] = [];
  @Input() public actionIds: number[] = [];
  @Input() public perimeterIds: number[] = [];
  @Input() public perimeterLabel: string = '';
  @Input() public displayAsDialog: boolean = true;
  @Input() public fromWorkflow: boolean = false;
  @Input() public selectionMode: boolean = false;
  @Input() public linkedTypeFilter: 'asset' | 'investment' | 'initiative' | null = null;
  @Input() public loadAll: boolean = false;

  public loading: boolean = true;
  public gridOptions: GridOptions = {};
  private gridApi: GridApi;
  private subscriptions: Subscription[] = [];
  public selectedDoc: any = null;

  public ngOnInit(): void {
    this.subscriptions.push(
      this.spreadsheetColumnsService.getAgGridTranslations()
      .subscribe((agGridTranslations) => {

        this.documentsService._documentsHaveChanged$.subscribe(() => {
          this.loading = true;
          this.loadData()
        });

        this.gridOptions = {
          ...this.gridOptions,
          localeText: agGridTranslations,
          loading: false,
          context: this,
          rowHeight: 38,
          headerHeight: 40,
          rowBuffer: 10, // Draw 10 rows above and below the visible rows (Performance)
          allowDragFromColumnsToolPanel: true, // Allow user to move columns from panel
          cellSelection: false, // Unable rows/columns range select
          rowSelection: 'multiple', // Enable to select the entire row instead of a cell
          suppressCellFocus: true,
          rowStyle: { cursor: 'pointer' },
          onRowClicked: (params) => {
            if (this.selectionMode) {
              this.ref.close(params.data);
            } else {
              this.openDocumentSheet(params.data);
            }
          },
          getRowId: params => params.data?.id?.toString(),
          suppressPropertyNamesCheck: true,
          columnMenu: 'new',
          icons: {
            filter: TABLE_FILTER_ICON,
          },
          defaultColDef: {
            sortable: true,
            resizable: true,
            editable: false,
            filter: true,
            menuTabs: [],
            suppressHeaderMenuButton: true,
            enableCellChangeFlash: false,
            minWidth: 100,
          },
          columnDefs: [
            {
              field: 'title',
              headerName: this.translate.instant('Linked document label'),
              flex: 3,
              minWidth: 160,
            },
            {
              field: 'type',
              headerName: this.translate.instant('Document type'),
              flex: 2,
              minWidth: 130,
            },
            {
              field: 'linked_to_type',
              headerName: this.translate.instant('documents.attached_to'),
              width: 100,
              minWidth: 80,
            },
            ...(!this.selectionMode ? [{
              field: 'delete',
              headerName: '',
              cellRenderer: DeleteRendererComponent,
              width: 50,
              sortable: false,
              filter: false,
              suppressMovable: true,
              pinned: 'right' as const,
            }] : []),
          ],
          animateRows: true, // Row animations (when expanding)
          // Always show the column menus
          suppressMenuHide: true,
          groupHeaderHeight: 28, // Hide the columns groups in the header (Set it to 20 to show)
          // Restrict aggregations some functions (sum in an ag-grid native one)
          onGridReady: (params) => {
            this.gridApi = params.api;
            this.gridApi.sizeColumnsToFit();
            this.loadData();
          },
          onFirstDataRendered: () => {
            this.loading = false;
            this.cdRef.detectChanges();
            this.gridApi.sizeColumnsToFit();
          },
          onGridSizeChanged: () => {
            this.gridApi?.sizeColumnsToFit();
          },
        };
      }),
    );
  }

  public close(): void {
    if (this.selectionMode && this.selectedDoc) {
      this.ref.close(this.selectedDoc);
    } else {
      this.ref.close();
    }
  }

  private loadAgGridData(): Promise<any> {
    return new Promise((resolve) => {
      const source$ = this.loadAll
        ? this.documentsService.loadDocumentsWithParams({}).pipe(
            map((docs: any[]) => [[], [], [], docs])
          )
        : this.documentsService.getDocuments(
            this.assetIds,
            this.investIds,
            this.actionIds,
            this.perimeterIds,
          );
      this.subscriptions.push(
        source$.subscribe(([assetsDocs, invDocs,  actionsDoc, perimeterDocs]) => {
          let allDocs = [
            assetsDocs,
            invDocs,
            actionsDoc,
            perimeterDocs,
          ].filter((docs) => docs?.length).reduce((acc, docs) => {
            return [
              ...acc,
              ...docs.map((doc) => ({
                ...doc,
                name: doc.title,
                linked_to_type: this.translate.instant(
                  doc.asset_label
                    ? 'documents.linked_to_type.equipment' : doc.investment_label
                      ? 'Investment' : doc.initiative_label
                        ? 'Action' : doc.perimeter_label
                          ? 'Perimeter' : '-'
                ),
                linked_to_label: doc.asset_label || doc.investment_label || doc.initiative_label || doc.perimeter_label || '-',
                _entity_type: doc.asset_label ? 'asset' : doc.investment_label ? 'investment' : doc.initiative_label ? 'initiative' : 'perimeter',
              })),
            ];
          }, [] as any[]);
          if (this.linkedTypeFilter) {
            allDocs = allDocs.filter((doc) => doc._entity_type === this.linkedTypeFilter);
          }
          // Deduplicate by title + type + link in selection mode
          if (this.selectionMode) {
            const seen = new Set<string>();
            allDocs = allDocs.filter((doc) => {
              const key = `${doc.title}||${doc.type}||${doc.link || ''}`;
              if (seen.has(key)) return false;
              seen.add(key);
              return true;
            });
          }
          resolve(allDocs);
        }),
      );
    });
  }

  private loadData() {
    this.loadAgGridData().then((rowData) => {
      this.gridApi?.setGridOption('rowData', rowData);
      this.loading = false;
      this.cdRef.detectChanges();
    });
  }

  public openDocumentSheet(doc: any): void {
    const dialogRef = this.sideFormLevelService.open(DocumentSheetComponent, {
      autoFocus: false,
      closeOnBackdropClick: false,
      hasBackdrop: true,
      context: {
        doc,
        siteId: this.perimeterIds && this.perimeterIds.length > 0 ? this.perimeterIds[0] : null
      },
    });
    dialogRef.onClose.pipe(first()).subscribe(() => {
      this.documentsService.clearDocumentsCache();
      this.loadData();
    });
  }

  public delete(id: number, title: string): void {
    const dialog = this.dialogService.open(BasicDialogComponent, {
      autoFocus: false,
      context: {
        title: this.translate.instant('Document deletion'),
        content: this.translate.instant('Do you confirm that {elementName} will be deleted ?', { elementName: title }),
      },
    });
    dialog.onClose.pipe(
      filter((confirmed) => !!confirmed),
      switchMap(() => {
        this.saveToast.showSaving();
        return this.documentsService.deleteDocument(id);
      }),
    ).subscribe({
      next: () => {
        const rowNode = this.gridApi.getRowNode(id.toString());
        if (rowNode) {
          this.gridApi.applyTransaction({ remove: [rowNode.data] });
        }
        this.documentsService.documentsHaveChanged();
        this.saveToast.showSaved();
      },
      error: () => {
        this.saveToast.close();
        this.loadData();
      },
    });
  }

  public addNewDocument(): void {
    this.openAddDocumentForm('');
  }

  private openAddDocumentForm(initialLabel: string): void {
    this.sideFormLevelService.open(
      AddDocumentDialogComponent,
      {
        autoFocus: false,
        closeOnBackdropClick: false,
        hasBackdrop: true,
        dialogClass: 'add-document-panel-overlay',
        context: {
          investIds: this.investIds,
          perimeterIds: this.perimeterIds || [],
          perimeterLabel: this.perimeterLabel,
          siteId: this.perimeterIds && this.perimeterIds.length > 0 ? this.perimeterIds[0] : null,
          initialLabel,
        },
      },
    );
  }

}
